import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ShoppingCart,
  Users,
  Building2,
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle,
  MessageSquare,
  Truck,
  ArrowUpRight,
  ArrowDownRight,
  RefreshCw,
  Calendar,
  FileText,
  Settings,
  BarChart3,
  DollarSign,
  ClipboardList,
  Activity
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { getOrders } from '../../api/wholesale/orders';
import { getOutstandingBalances } from '../../api/wholesale/payments';
import { getDashboardStats, getCompanyLoadingStats } from '../../api/wholesale/analytics';
import { useFiscalPeriod } from '../../contexts/FiscalPeriodContext';
import PeriodFilter from '../../components/common/PeriodFilter';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, change, changeType, color, link }) => {
  const colorClasses = {
    emerald: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
    blue: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
    amber: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
    purple: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
    red: 'bg-red-500/10 text-red-500 border-red-500/20'
  };

  const Card = link ? Link : 'div';
  const cardProps = link ? { to: link } : {};

  return (
    <Card
      {...cardProps}
      className={`bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow ${link ? 'cursor-pointer' : ''}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-gray-500 text-sm font-medium">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
          {change && (
            <div
              className={`flex items-center gap-1 mt-2 text-sm ${
                changeType === 'up' ? 'text-emerald-600' : 'text-red-600'
              }`}
            >
              {changeType === 'up' ? (
                <ArrowUpRight className="w-4 h-4" />
              ) : (
                <ArrowDownRight className="w-4 h-4" />
              )}
              <span>{change}</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </Card>
  );
};

// Quick Action Card Component
const QuickActionCard = ({ title, description, icon: Icon, link, color }) => {
  const colorClasses = {
    emerald: 'bg-emerald-500 hover:bg-emerald-600',
    blue: 'bg-blue-500 hover:bg-blue-600',
    amber: 'bg-amber-500 hover:bg-amber-600',
    purple: 'bg-purple-500 hover:bg-purple-600',
    indigo: 'bg-indigo-500 hover:bg-indigo-600',
    cyan: 'bg-cyan-500 hover:bg-cyan-600'
  };

  const hoverColorClasses = {
    emerald: 'hover:border-emerald-500 hover:bg-emerald-50',
    blue: 'hover:border-blue-500 hover:bg-blue-50',
    amber: 'hover:border-amber-500 hover:bg-amber-50',
    purple: 'hover:border-purple-500 hover:bg-purple-50',
    indigo: 'hover:border-indigo-500 hover:bg-indigo-50',
    cyan: 'hover:border-cyan-500 hover:bg-cyan-50'
  };

  return (
    <Link
      to={link}
      className={`group bg-white rounded-xl p-4 border-2 border-gray-200 shadow-md hover:shadow-lg transition-all flex items-center gap-4 ${hoverColorClasses[color]}`}
    >
      <div className={`p-3 rounded-lg ${colorClasses[color]} text-white transition-colors`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="font-semibold text-gray-900 group-hover:text-gray-900 transition-colors">
          {title}
        </h4>
        <p className="text-sm text-gray-500 truncate">{description}</p>
      </div>
      <ArrowUpRight className="w-5 h-5 text-gray-400 group-hover:text-gray-600 transition-colors" />
    </Link>
  );
};

// Recent Orders Card
const RecentOrdersCard = ({ orders, loading }) => {
  const statusColors = {
    pending: 'bg-amber-100 text-amber-700',
    confirmed: 'bg-blue-100 text-blue-700',
    forwarded: 'bg-purple-100 text-purple-700',
    in_transit: 'bg-indigo-100 text-indigo-700',
    delivered: 'bg-emerald-100 text-emerald-700',
    completed: 'bg-green-100 text-green-700'
  };

  return (
    <div className="bg-white rounded-xl shadow-md">
      <div className="flex items-center justify-between p-4 border-b border-gray-100">
        <h3 className="font-semibold text-gray-900">Recent Orders</h3>
        <Link
          to="/wholesale/orders"
          className="text-emerald-600 text-sm font-medium hover:text-emerald-700"
        >
          View All
        </Link>
      </div>
      {loading ? (
        <div className="p-8 text-center">
          <RefreshCw className="w-8 h-8 mx-auto text-gray-300 animate-spin" />
        </div>
      ) : orders.length === 0 ? (
        <div className="p-8 text-center text-gray-500">
          <ShoppingCart className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>No orders yet</p>
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {orders.map((order) => (
            <Link
              key={order.id}
              to={`/wholesale/orders/${order.id}`}
              className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
            >
              <div>
                <p className="font-medium text-gray-900">{order.order_number}</p>
                <p className="text-sm text-gray-500">
                  {order.distributor_name || order.business_name}
                </p>
              </div>
              <div className="text-right">
                <span
                  className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${statusColors[order.order_status || order.status] || 'bg-gray-100 text-gray-700'}`}
                >
                  {(order.order_status || order.status || 'unknown').replace('_', ' ')}
                </span>
                <p className="text-sm text-gray-500 mt-1">
                  ₦{(order.total_amount || order.total || 0).toLocaleString()}
                </p>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

// Trend Chart Component
const TrendChart = ({ title, data, dataKey, color, valuePrefix = '', valueSuffix = '' }) => {
  const formatValue = (value) => {
    if (value >= 1000000) {
      return `${valuePrefix}${(value / 1000000).toFixed(1)}M${valueSuffix}`;
    }
    if (value >= 1000) {
      return `${valuePrefix}${(value / 1000).toFixed(1)}K${valueSuffix}`;
    }
    return `${valuePrefix}${value}${valueSuffix}`;
  };

  return (
    <div className="bg-white rounded-xl shadow-md p-5">
      <h3 className="font-semibold text-gray-900 mb-4">{title}</h3>
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
            <defs>
              <linearGradient id={`gradient-${dataKey}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={color} stopOpacity={0.3} />
                <stop offset="95%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 11, fill: '#9CA3AF' }}
              axisLine={{ stroke: '#E5E7EB' }}
              tickLine={false}
            />
            <YAxis
              tick={{ fontSize: 11, fill: '#9CA3AF' }}
              axisLine={false}
              tickLine={false}
              tickFormatter={formatValue}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#fff',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }}
              formatter={(value) => [formatValue(value), title]}
            />
            <Area
              type="monotone"
              dataKey={dataKey}
              stroke={color}
              strokeWidth={2}
              fill={`url(#gradient-${dataKey})`}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// Orders Bar Chart Component
const OrdersBarChart = ({ data }) => {
  return (
    <div className="bg-white rounded-xl shadow-md p-5">
      <h3 className="font-semibold text-gray-900 mb-4">Orders by Status</h3>
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 11, fill: '#9CA3AF' }}
              axisLine={{ stroke: '#E5E7EB' }}
              tickLine={false}
            />
            <YAxis tick={{ fontSize: 11, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
            <Tooltip
              contentStyle={{
                backgroundColor: '#fff',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }}
            />
            <Bar dataKey="count" fill="#10B981" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const WholesaleDashboard = () => {
  const { periodRange, settings } = useFiscalPeriod();
  const [stats, setStats] = useState({
    totalOrders: 0,
    pendingOrders: 0,
    completedOrders: 0,
    totalRevenue: 0,
    outstandingBalance: 0,
    activeDistributors: 0,
    activeCompanies: 0
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Local period filter state (null = use global, otherwise override)
  const [localPeriod, setLocalPeriod] = useState(null);

  // Current active date range (local override or global)
  const activeDateRange = useMemo(() => {
    if (localPeriod) {
      return {
        start_date: localPeriod.start_date || null,
        end_date: localPeriod.end_date || null,
        label: localPeriod.label
      };
    }
    return {
      start_date: periodRange?.start_date || null,
      end_date: periodRange?.end_date || null,
      label: periodRange?.period_label || 'All Time'
    };
  }, [localPeriod, periodRange]);

  // Determine if user explicitly chose "All Time" (local override with no dates)
  const isExplicitAllTime = localPeriod && !localPeriod.start_date && !localPeriod.end_date;

  // Handle period filter change

  const handlePeriodChange = useCallback((range) => {
    if (range.isGlobal) {
      setLocalPeriod(null);
    } else {
      setLocalPeriod(range);
    }
  }, []);

  // Trend data (mock data - in production this would come from API)
  const [revenueTrend, setRevenueTrend] = useState([]);
  const [ordersTrend, setOrdersTrend] = useState([]);
  const [ordersByStatus, setOrdersByStatus] = useState([]);
  const [companyLoadingStats, setCompanyLoadingStats] = useState({
    companies: [],
    totals: { total_cartons: 0, total_value: 0 }
  });

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Build date params from active date range
      const dateParams = {};
      if (activeDateRange.start_date) dateParams.start_date = activeDateRange.start_date;
      if (activeDateRange.end_date) dateParams.end_date = activeDateRange.end_date;

      // Tell backend to skip global fiscal period fallback when "All Time" is explicitly selected
      if (isExplicitAllTime && !dateParams.start_date && !dateParams.end_date) {
        dateParams.ignore_global_period = true;
      }

      // Fetch data in parallel with fiscal period filter
      const [statsRes, ordersRes, loadingStatsRes, outstandingBalancesRes] =
        await Promise.allSettled([
          getDashboardStats(dateParams),
          getOrders({ limit: 200, sort: 'created_at', order: 'desc', ...dateParams }), // Fetch up to 200 for trends and recent list
          getCompanyLoadingStats(dateParams),
          getOutstandingBalances()
        ]);

      // Process stats from dedicated endpoint
      let statsData = {
        total_orders: 0,
        pending_orders: 0,
        total_revenue: 0,
        active_customers: 0,
        active_partners: 0
      };

      if (statsRes.status === 'fulfilled' && statsRes.value?.data) {
        statsData = statsRes.value.data;
      }

      // Process outstanding balances
      let outstandingBalances = [];
      if (outstandingBalancesRes.status === 'fulfilled') {
        outstandingBalances =
          outstandingBalancesRes.value?.data || outstandingBalancesRes.value || [];
        if (!Array.isArray(outstandingBalances)) outstandingBalances = [];
      }

      // Process recent orders
      let orders = [];
      if (ordersRes.status === 'fulfilled') {
        orders = ordersRes.value?.data || ordersRes.value || [];
        if (!Array.isArray(orders)) orders = [];
      }

      // Calculate outstanding balance from all customers using the dedicated endpoint
      const totalOutstandingBalance = outstandingBalances.reduce((sum, balance) => {
        // Only use total_outstanding (calculated from actual unpaid orders)
        return sum + (parseFloat(balance.total_outstanding) || 0);
      }, 0);

      setStats({
        totalOrders: statsData.total_orders,
        pendingOrders: statsData.pending_orders,
        completedOrders: statsData.completed_orders || 0,
        totalRevenue: statsData.total_revenue,
        outstandingBalance: totalOutstandingBalance,
        activeDistributors: statsData.active_customers,
        activeCompanies: statsData.active_partners
      });

      setRecentOrders(orders.slice(0, 5));

      // Process company loading stats
      if (loadingStatsRes.status === 'fulfilled' && loadingStatsRes.value?.data) {
        setCompanyLoadingStats(loadingStatsRes.value.data);
      }

      // Generate trend data from orders
      generateTrendData(orders);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [activeDateRange.start_date, activeDateRange.end_date]);

  const generateTrendData = (orders) => {
    // Group orders by month for revenue trend
    const monthlyData = {};
    const statusCounts = {};

    orders.forEach((order) => {
      const date = new Date(order.created_at);
      const monthKey = date.toLocaleDateString('en-US', { month: 'short' });

      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = { revenue: 0, orders: 0 };
      }
      monthlyData[monthKey].revenue += parseFloat(order.total_amount) || 0;
      monthlyData[monthKey].orders += 1;

      const status = order.order_status || order.status || 'unknown';
      statusCounts[status] = (statusCounts[status] || 0) + 1;
    });

    // Convert to arrays for charts
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec'
    ];
    const currentMonth = new Date().getMonth();
    const last6Months = [];
    for (let i = 5; i >= 0; i--) {
      const monthIndex = (currentMonth - i + 12) % 12;
      last6Months.push(months[monthIndex]);
    }

    const revenueTrendData = last6Months.map((month) => ({
      name: month,
      revenue: monthlyData[month]?.revenue || 0
    }));

    const ordersTrendData = last6Months.map((month) => ({
      name: month,
      orders: monthlyData[month]?.orders || 0
    }));

    const statusData = Object.entries(statusCounts).map(([name, count]) => ({
      name: name.replace('_', ' ').charAt(0).toUpperCase() + name.slice(1).replace('_', ' '),
      count
    }));

    setRevenueTrend(revenueTrendData);
    setOrdersTrend(ordersTrendData);
    setOrdersByStatus(statusData);
  };

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Quick actions configuration
  const quickActions = [
    {
      title: 'Sales Report',
      description: 'View detailed sales analytics',
      icon: BarChart3,
      link: '/wholesale/reports/sales',
      color: 'emerald'
    },
    {
      title: 'Distributor Report',
      description: 'Customer performance analysis',
      icon: Users,
      link: '/wholesale/reports/distributors',
      color: 'blue'
    },
    {
      title: 'Revenue Report',
      description: 'Track revenue and growth',
      icon: DollarSign,
      link: '/wholesale/reports/revenue',
      color: 'purple'
    },
    {
      title: 'Audit Logs',
      description: 'View system activity history',
      icon: ClipboardList,
      link: '/wholesale/settings/audit-logs',
      color: 'amber'
    },
    {
      title: 'Fiscal Periods',
      description: 'Manage accounting periods',
      icon: Calendar,
      link: '/wholesale/settings/fiscal-periods',
      color: 'indigo'
    },
    {
      title: 'Activity Monitor',
      description: 'Real-time system status',
      icon: Activity,
      link: '/wholesale/settings/audit-logs',
      color: 'cyan'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Wholesale Dashboard</h1>
          <p className="text-gray-500 mt-1">
            Overview of your wholesale operations
            {localPeriod && (
              <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                {activeDateRange.label}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Period Filter */}
          <PeriodFilter
            onChange={handlePeriodChange}
            showLabel={true}
            compact={false}
            storageKey="wholesale_dashboard"
          />
          <button
            onClick={fetchDashboardData}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Orders"
          value={stats.totalOrders.toLocaleString()}
          icon={ShoppingCart}
          color="emerald"
          link="/wholesale/orders"
        />
        <StatCard
          title="Pending Orders"
          value={stats.pendingOrders}
          icon={Clock}
          color="amber"
          link="/wholesale/orders?status=pending"
        />
        <StatCard
          title="Total Revenue"
          value={`₦${(stats.totalRevenue / 1000000).toFixed(1)}M`}
          icon={TrendingUp}
          color="blue"
        />
        <StatCard
          title="Outstanding Balance"
          value={`₦${(stats.outstandingBalance / 1000000).toFixed(1)}M`}
          icon={AlertCircle}
          color="red"
          link="/wholesale/orders?payment_status=outstanding"
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <StatCard
          title="Active Customers"
          value={stats.activeDistributors}
          icon={Users}
          color="purple"
          link="/wholesale/distributors"
        />
        <StatCard
          title="Manufacturing Partners"
          value={stats.activeCompanies}
          icon={Building2}
          color="blue"
          link="/wholesale/companies"
        />
      </div>

      {/* Company Loading Stats */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Company Loading Stats</h2>
            <p className="text-sm text-gray-500">Cartons loaded from each company</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-emerald-600">
              {companyLoadingStats.totals?.total_cartons?.toLocaleString() || 0}
            </p>
            <p className="text-sm text-gray-500">Total Cartons</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {companyLoadingStats.companies
            ?.filter((c) => c.total_cartons > 0)
            .map((company) => (
              <Link
                key={company.company_id}
                to={`/wholesale/companies/${company.company_id}`}
                className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-gray-900 truncate">{company.company_name}</h4>
                    <p className="text-xs text-gray-500">{company.total_orders} orders</p>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-emerald-600">
                      {parseInt(company.total_cartons).toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500">
                      ₦{(parseFloat(company.total_value) / 1000000).toFixed(1)}M
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          {(!companyLoadingStats.companies ||
            companyLoadingStats.companies.filter((c) => c.total_cartons > 0).length === 0) && (
            <div className="col-span-full text-center py-8 text-gray-500">
              <Building2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No loading data available</p>
            </div>
          )}
        </div>
        <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
          <span className="text-sm text-gray-500">Combined Value:</span>
          <span className="text-lg font-bold text-blue-600">
            ₦{((companyLoadingStats.totals?.total_value || 0) / 1000000).toFixed(2)}M
          </span>
        </div>
      </div>

      {/* Quick Actions Section */}
      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Quick Actions</h2>
            <p className="text-sm text-gray-500">Access reports and settings</p>
          </div>
          <Link
            to="/wholesale/settings"
            className="text-sm text-emerald-600 hover:text-emerald-700 font-medium flex items-center gap-1"
          >
            <Settings className="w-4 h-4" />
            All Settings
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {quickActions.map((action, index) => (
            <QuickActionCard
              key={index}
              title={action.title}
              description={action.description}
              icon={action.icon}
              link={action.link}
              color={action.color}
            />
          ))}
        </div>
      </div>

      {/* Trend Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <TrendChart
          title="Revenue Trend"
          data={revenueTrend}
          dataKey="revenue"
          color="#10B981"
          valuePrefix="₦"
        />
        <TrendChart title="Orders Trend" data={ordersTrend} dataKey="orders" color="#3B82F6" />
      </div>

      {/* Orders by Status Chart */}
      {ordersByStatus.length > 0 && <OrdersBarChart data={ordersByStatus} />}

      {/* Recent Orders */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RecentOrdersCard orders={recentOrders} loading={false} />

        {/* Top Distributors Card */}
        <div className="bg-white rounded-xl shadow-md">
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900">Performance Insights</h3>
            <Link
              to="/wholesale/reports/distributors"
              className="text-emerald-600 text-sm font-medium hover:text-emerald-700"
            >
              View Report
            </Link>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-500 rounded-lg">
                    <TrendingUp className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Total Revenue</p>
                    <p className="text-sm text-gray-500">This period</p>
                  </div>
                </div>
                <p className="text-xl font-bold text-emerald-600">
                  ₦{(stats.totalRevenue / 1000000).toFixed(2)}M
                </p>
              </div>

              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-500 rounded-lg">
                    <ShoppingCart className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Avg Order Value</p>
                    <p className="text-sm text-gray-500">Per transaction</p>
                  </div>
                </div>
                <p className="text-xl font-bold text-blue-600">
                  ₦
                  {stats.totalOrders > 0
                    ? (stats.totalRevenue / stats.totalOrders / 1000).toFixed(0)
                    : 0}
                  K
                </p>
              </div>

              <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-500 rounded-lg">
                    <CheckCircle className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">Completion Rate</p>
                    <p className="text-sm text-gray-500">Orders delivered</p>
                  </div>
                </div>
                <p className="text-xl font-bold text-purple-600">
                  {stats.totalOrders > 0
                    ? ((stats.completedOrders / stats.totalOrders) * 100).toFixed(0)
                    : 0}
                  %
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WholesaleDashboard;
